import { useEffect, useRef, useState } from 'react';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { initializeApp, getApps } from 'firebase/app';
import { useAuth } from '../lib/auth';
import { api } from '@/lib/api';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export interface ForegroundNotification {
  title: string;
  body: string;
  deepLink?: string;
}

/**
 * Registers the service worker, asks for notification permission once the user
 * is signed in, and syncs the FCM token with the backend.
 */
export function useFcm() {
  const { isSignedIn } = useAuth();
  const [notification, setNotification] = useState<ForegroundNotification | null>(null);
  const registered = useRef(false);

  useEffect(() => {
    if (!isSignedIn || registered.current) return;
    if (!firebaseConfig.apiKey || !import.meta.env.VITE_FIREBASE_VAPID_KEY) return;

    let unsubscribe: (() => void) | undefined;

    (async () => {
      if (!(await isSupported().catch(() => false))) return;
      if (!('serviceWorker' in navigator)) return;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const app = getApps().length ? getApps()[0]! : initializeApp(firebaseConfig);
      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const messaging = getMessaging(app);

      const token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: swReg,
      }).catch(() => null);

      if (token) {
        registered.current = true;
        await api.registerDevice(token, 'web').catch(() => {});
      }

      unsubscribe = onMessage(messaging, (payload) => {
        setNotification({
          title: payload.notification?.title ?? 'Yalıda Davet',
          body: payload.notification?.body ?? '',
          deepLink: payload.data?.deepLink,
        });
      });
    })();

    return () => unsubscribe?.();
  }, [isSignedIn]);

  return { notification, dismiss: () => setNotification(null) };
}
